"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, CircleCheck as CheckCircle2, Award } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const accreditations = [{
  name: "NASC Accredited"
}, {
  name: "CHAS Approved"
}];
const capabilities = [{
  label: "Commercial & developments"
}, {
  label: "Residential & renovations"
}, {
  label: "Roofing & chimney scaffolds"
}, {
  label: "Specialist & temporary roofs"
}];
const sitePhotoQuery = new WP_Query({
  post_type: "site_photo",
  per_page: 20
});
function HeroMarlowBackground() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="w-full h-full bg-muted animate-pulse" />;
  }
  const matchedPhoto = posts.find(post => post.title?.toLowerCase() === "marlow scaffolding hero image" || post.customFields?.photo_name?.toLowerCase() === "marlow scaffolding hero image");
  const heroImageSrc = matchedPhoto?.customFields?.image || matchedPhoto?.featuredImage;
  const heroImageAlt = matchedPhoto?.customFields?.alt_text || "Scaffolding installation by BMF Scaffolding Ltd in Marlow";
  if (heroImageSrc) {
    const customImage = {
      src: heroImageSrc,
      srcset: "",
      sizes: ""
    };
    return <img src={customImage.src} data-wvc-srcset={customImage.srcset} data-wvc-sizes={customImage.sizes} alt={heroImageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />;
  }
  return <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/hero_scaffolding_marlow.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/hero_scaffolding_marlow.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/hero_scaffolding_marlow.webp-wvc-sizes" alt="Scaffolding installation by BMF Scaffolding Ltd in Marlow" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />;
}
export default function HeroMarlow() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="heroscaffoldingmarlow" data-nav="dark" className="relative overflow-hidden bg-secondary text-secondary-foreground pt-32 pb-20 md:pt-40 md:pb-28 lg:pt-48 lg:pb-32 border-b border-sidebar-border">
      {/* Background architectural scaffolding imagery overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-20 mix-blend-luminosity">
        <PostsProvider wp_query={{
        post_type: "site_photo",
        per_page: 20,
        status: ["publish"]
      }}>
          <HeroMarlowBackground />
        </PostsProvider>
      </div>

      {/* Subtle radial ambient gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-secondary/80 via-secondary to-secondary pointer-events-none" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Main Hero Content */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-8 flex flex-col items-start text-left space-y-6">
            {/* Location Tag */}
            <div className="inline-flex items-center gap-2 rounded-md bg-sidebar-accent px-3 py-1 text-xs font-medium text-sidebar-foreground border border-sidebar-border">
              <ShieldCheck className="size-3.5 text-primary" />
              <span>Marlow & South Buckinghamshire</span>
            </div>

            {/* H1 Heading */}
            <h1 className="font-default text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-secondary-foreground leading-tight">
              Scaffolding in Marlow
            </h1>

            {/* Supporting Proposition */}
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl font-normal leading-relaxed">
              Safe, fully compliant scaffolding for commercial sites, residential renovations, roofing contractors, and complex access requirements across Marlow and the Home Counties.
            </p>

            {/* Key Capabilities Pills */}
            <div className="grid grid-cols-2 sm:grid-cols-2 gap-2.5 pt-2 w-full max-w-xl">
              {capabilities.map((item, i) => <div key={item.label} data-index={i} className="flex items-center gap-2 text-sm text-secondary-foreground/90 font-medium">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>{item.label}</span>
                </div>)}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4 w-full sm:w-auto">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-md px-6">
                <Link to="/ContactRequestQuotePage">
                  <span>Request a quote</span>
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-sidebar-border bg-sidebar hover:bg-sidebar-accent text-sidebar-foreground hover:text-sidebar-accent-foreground font-medium px-6">
                <a href="tel:01344929900" className="text-sidebar-foreground hover:text-sidebar-accent-foreground">
                  <Phone className="size-4 mr-2 text-primary" />
                  <span>Call 01344 929900</span>
                </a>
              </Button>
            </div>

            {/* Confirmed Accreditations Strip */}
            <div className="pt-6 border-t border-sidebar-border/60 w-full">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
                Industry accreditations & safety standards
              </p>
              <div className="flex flex-wrap items-center gap-3">
                {accreditations.map((acc, i) => <Badge key={acc.name} data-index={i} variant="outline" className="bg-secondary/60 text-secondary-foreground border-sidebar-border px-3 py-1 font-medium text-xs flex items-center gap-1.5">
                    <Award className="size-3 text-primary" />
                    <span>{acc.name}</span>
                  </Badge>)}
              </div>
            </div>
          </motion.div>

          {/* Quick Summary Card / Trust Sideboard */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.15,
          ease: "easeOut"
        }} className="lg:col-span-4">
            <div className="rounded-xl border border-sidebar-border bg-sidebar/90 p-6 sm:p-8 backdrop-blur-xs shadow-xl space-y-5">
              <div className="space-y-2">
                <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                  BMF Scaffolding Ltd
                </span>
                <h2 className="font-default text-xl font-bold text-sidebar-foreground">
                  Safety-led scaffolding contractor
                </h2>
                <p className="text-sm text-muted-foreground leading-normal">
                  Over 20 years of hands-on operational leadership. Committed to rigorous safety, punctual delivery, and direct accountability.
                </p>
              </div>

              <div className="space-y-3 pt-2 text-xs border-t border-sidebar-border text-sidebar-foreground/80">
                <div className="flex justify-between items-center py-1">
                  <span className="text-muted-foreground">Emergency & planned works</span>
                  <span className="font-semibold text-sidebar-foreground">Available</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-muted-foreground">Coverage</span>
                  <span className="font-semibold text-sidebar-foreground">Marlow & SL7</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-muted-foreground">Safety compliance</span>
                  <span className="font-semibold text-sidebar-foreground">NASC / TG20</span>
                </div>
              </div>

              <div className="pt-2">
                <Button asChild variant="secondary" className="w-full bg-sidebar-accent hover:bg-sidebar-accent/80 text-sidebar-accent-foreground border border-sidebar-border justify-center">
                  <Link to="/ProjectsCaseStudiesPage">
                    <span>View project case studies</span>
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}